/*
 * Prompt Service
 * Features: CRUD operations for user prompt templates
 * Logic: Manages prompt library via API endpoints with filtering and usage tracking
 * Changes:
 * - Initial implementation supporting categories, favorites, and search
 * - Simplified createPrompt() to only require promptContent (title auto-generated by database)
 * - Updated Prompt interface from PascalCase to camelCase properties (id, title, description, etc.)
 * - Database auto-generates unique titles: appends counter (e.g., "Title (1)") if duplicate detected
 */

import { request } from './dataService';

export interface Prompt {
  id: number;
  userId: number;
  title: string;
  description?: string;
  promptContent: string;
  category?: string;
  tags?: string;
  isFavorite: boolean;
  useCount: number;
  lastUsedAt?: string;
  createdAt: string;
  createdBy: number;
  updatedAt: string;
  updatedBy: number;
}

class PromptService {
  /**
   * Get all prompts with optional filters
   */
  async getPrompts(
    category?: string,
    favoritesOnly = false,
    searchTerm?: string
  ): Promise<Prompt[]> {
    const params: any = {};
    if (category) params.category = category;
    if (favoritesOnly) params.favoritesOnly = true;
    if (searchTerm) params.searchTerm = searchTerm;

    const queryString = new URLSearchParams(params).toString();
    const url = `/api/data/prompts${queryString ? '?' + queryString : ''}`;

    const response = await request(`get ${url}`);
    return response as Prompt[];
  }

  /**
   * Get specific prompt by ID
   */
  async getPrompt(id: number): Promise<Prompt> {
    const response = await request(`get /api/data/prompts/${id}`);
    const prompts = response as Prompt[];
    if (prompts.length === 0) {
      throw new Error(`Prompt ${id} not found`);
    }
    return prompts[0];
  }

  /**
   * Create new prompt
   */
  async createPrompt(promptContent: string): Promise<Prompt> {
    const response = await request('post /api/data/prompts', {
      promptContent,
    });
    // Response is an array from stored procedure: [{Id: number}]
    // After camelCase conversion: [{id: number}]
    const result = Array.isArray(response) ? response[0] : response;
    // Return newly created prompt by fetching it
    return this.getPrompt(result.id);
  }

  /**
   * Update existing prompt
   */
  async updatePrompt(
    id: number,
    updates: {
      title?: string;
      description?: string;
      promptContent?: string;
      category?: string;
      tags?: string;
      isFavorite?: boolean;
    }
  ): Promise<void> {
    await request(`put /api/data/prompts/${id}`, updates);
  }

  /**
   * Delete prompt
   */
  async deletePrompt(id: number): Promise<void> {
    await request(`delete /api/data/prompts/${id}`);
  }

  /**
   * Increment prompt use count
   */
  async incrementUseCount(id: number): Promise<number> {
    const response = await request(`post /api/data/prompts/${id}/use`);
    const result = response as any;
    return result.UseCount;
  }

  /**
   * Get unique categories from all prompts
   */
  async getCategories(): Promise<string[]> {
    const prompts = await this.getPrompts();
    const categories = new Set<string>();
    prompts.forEach(p => {
      if (p.category) categories.add(p.category);
    });
    return Array.from(categories).sort();
  }

  /**
   * Get favorite prompts
   */
  async getFavorites(): Promise<Prompt[]> {
    return this.getPrompts(undefined, true);
  }
}

export const promptService = new PromptService();
